import {AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon, Loader2Icon} from "lucide-react";
import {useEffect, useState} from "react";

import {Button} from "@components/ui/button";
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from "@components/ui/sheet";
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from "@components/ui/table";
import {
    STAFFING_DETAIL_PAGE_SIZE,
    useStaffingActualsDetail,
} from "@hooks/use-staffing-margin";

interface StaffingMarginDrilldownSheetProps {
    client: string | null;
    onClose: () => void;
}

function asNumber(value: string | number | null | undefined): number {
    const n = typeof value === "number" ? value : Number(value ?? 0);
    return Number.isFinite(n) ? n : 0;
}

function money(value: string | number | null | undefined): string {
    return asNumber(value).toLocaleString("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
    });
}

function num(value: string | number | null | undefined): string {
    return asNumber(value).toLocaleString("en-AU", {maximumFractionDigits: 0});
}

function pct(value: string | number | null | undefined): string {
    return `${asNumber(value).toFixed(2)}%`;
}

function dec(value: string | number | null | undefined, digits = 2): string {
    return asNumber(value).toLocaleString("en-AU", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

export function StaffingMarginDrilldownSheet({
    client,
    onClose,
}: StaffingMarginDrilldownSheetProps) {
    const [page, setPage] = useState(1);

    // Reset to page 1 whenever a different customer is opened.
    useEffect(() => {
        setPage(1);
    }, [client]);

    const {data, error, isLoading} = useStaffingActualsDetail(client, page);

    const totalRows = data?.total_rows ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalRows / STAFFING_DETAIL_PAGE_SIZE));
    const recon = data?.reconciliation;

    return (
        <Sheet open={Boolean(client)} onOpenChange={(open) => !open && onClose()}>
            <SheetContent
                side="right"
                className="flex w-[1100px] max-w-[97vw] flex-col gap-0 p-0 sm:max-w-[1100px]"
            >
                <SheetHeader className="border-b px-5 py-4 text-left">
                    <SheetTitle className="text-base font-semibold">
                        {client || "(blank customer)"}
                    </SheetTitle>
                    <SheetDescription>
                        Invoice-line detail from the promoted Gross Margin CSV — auditable source rows.
                    </SheetDescription>
                </SheetHeader>

                <div className="min-h-0 flex-1 overflow-auto p-4">
                    {isLoading && !data && (
                        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                            <Loader2Icon className="mr-2 size-4 animate-spin" />
                            Loading invoice-line detail…
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center justify-center py-12 text-sm text-destructive">
                            <AlertCircleIcon className="mr-2 size-4" />
                            Could not load detail rows.
                        </div>
                    )}

                    {!error && data && data.rows.length === 0 && (
                        <p className="py-12 text-center text-sm text-muted-foreground">
                            No invoice lines found for this customer.
                        </p>
                    )}

                    {!error && data && data.rows.length > 0 && (
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/40">
                                        <TableHead>Invoice</TableHead>
                                        <TableHead>Timesheet</TableHead>
                                        <TableHead>Shift date</TableHead>
                                        <TableHead>Site</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Shift</TableHead>
                                        <TableHead>Day</TableHead>
                                        <TableHead className="text-right">Hours</TableHead>
                                        <TableHead className="text-right">Invoice total</TableHead>
                                        <TableHead className="text-right">Gross wages</TableHead>
                                        <TableHead className="text-right">Super</TableHead>
                                        <TableHead className="text-right">Payroll tax</TableHead>
                                        <TableHead className="text-right">WorkCover</TableHead>
                                        <TableHead className="text-right">Profit</TableHead>
                                        <TableHead className="text-right">Margin</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.rows.map((r) => (
                                        <TableRow key={r.source_row_number}>
                                            <TableCell className="font-medium">{r.invoice_no || "—"}</TableCell>
                                            <TableCell>{r.tsheet_no || "—"}</TableCell>
                                            <TableCell>{r.shift_date || "—"}</TableCell>
                                            <TableCell className="max-w-[160px] truncate">{r.service_location || "—"}</TableCell>
                                            <TableCell className="max-w-[160px] truncate">{r.pay_level || "—"}</TableCell>
                                            <TableCell>{r.shift_type || "—"}</TableCell>
                                            <TableCell>{r.day || "—"}</TableCell>
                                            <TableCell className="text-right tabular-nums">{dec(r.hours)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{money(r.invoice_total)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{money(r.gross_wages)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{money(r.superannuation)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{money(r.payroll_tax)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{money(r.workcover)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{money(r.profit)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{pct(r.margin_pct)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                {recon && (
                                    <TableFooter>
                                        <TableRow className="bg-muted/60">
                                            <TableCell colSpan={8} className="font-semibold">
                                                {num(recon.detail_count)} rows (whole customer)
                                            </TableCell>
                                            <TableCell className="text-right font-semibold tabular-nums">
                                                {money(recon.detail_total)}
                                            </TableCell>
                                            <TableCell colSpan={4} />
                                            <TableCell className="text-right font-semibold tabular-nums">
                                                {money(recon.detail_profit)}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between border-t px-5 py-3 text-sm">
                    <span className="text-muted-foreground">
                        {totalRows > 0
                            ? `Page ${page} of ${pageCount} · ${num(totalRows)} rows`
                            : ""}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1 || isLoading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <ChevronLeftIcon className="size-4" />
                            Prev
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= pageCount || isLoading}
                            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        >
                            Next
                            <ChevronRightIcon className="size-4" />
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
